import React from 'react';
import { Box, Typography, Chip, Button, Stack, IconButton, Menu, MenuItem, Divider, Tooltip, ToggleButtonGroup, ToggleButton, Paper } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';

export type ReadingMode = 'default' | 'eli5' | 'expert' | 'proscons';

interface BillHeroProps {
  code: string;
  title: string | undefined;
  statusBadge: string | React.ReactNode;
  lastAction?: string | null;
  progressPct?: number;
  // Optional per-step completion flags for the top tracker: [Introduced, House, Senate, President]
  stepStates?: boolean[];
  metadata?: { label: string; variant?: 'default' | 'outlined' }[];
  isFollowing?: boolean | null;
  onToggleFollow?: () => void;
  onChat?: () => void;
  onShare?: () => void;
  onCompare?: () => void;
  onRefresh?: () => void;
  onGenerateSummary?: () => void;
  onMoreAction?: (action: 'embed' | 'json' | 'csv' | 'rss') => void;
  readingMode?: ReadingMode;
  onReadingModeChange?: (mode: ReadingMode) => void;
  // New: prominent sponsor & cosponsor info
  sponsorName?: string;
  cosponsorCount?: number;
}

const BillHero: React.FC<BillHeroProps> = ({
  code,
  title,
  statusBadge,
  lastAction,
  progressPct = 0,
  stepStates,
  metadata = [],
  isFollowing,
  onToggleFollow,
  onChat,
  onShare,
  onCompare,
  onRefresh,
  onGenerateSummary,
  onMoreAction,
  readingMode = 'default',
  onReadingModeChange,
  sponsorName,
  cosponsorCount,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <header>
      <Paper elevation={0} square sx={{  bgcolor: (t)=> t.palette.mode==='dark' ? 'rgba(17,17,17,0.9)' : 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ mx: 'auto', maxWidth: '1200px', px: { xs: 2, md: 3 }, py: 3 }}>
          {/* Row 1 — Title at top */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              {title && (
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, fontSize: { xs: '1.45rem', md: '1.9rem' }, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                  {title}
                </Typography>
              )}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{code}</Typography>
                <Chip size="small" label={statusBadge} color="primary" variant="outlined" sx={{ borderRadius: 999 }} />
                {sponsorName && (
                  <Chip size="small" variant="outlined" label={`Sponsor: ${sponsorName}`} />
                )}
                {typeof cosponsorCount === 'number' && (
                  <Chip size="small" variant="outlined" label={`${cosponsorCount} cosponsor${cosponsorCount === 1 ? '' : 's'}`} />
                )}
              </Stack>
              {lastAction && <Typography variant="caption" sx={{ mt: 0.25, color: 'text.secondary' }}>{lastAction}</Typography>}
            </Box>
            <Box sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0 }}>
              {/* Progress rail */}
              <Box sx={{ position: 'relative', height: 8, bgcolor: 'action.hover', borderRadius: 999, overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, bgcolor: 'primary.main', width: `${progressPct}%`, transition: 'width 300ms ease' }} />
                {/* Step dots */}
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
                  {['Intro','House','Senate','President'].map((label, idx) => {
                    const done = Array.isArray(stepStates) ? Boolean(stepStates[idx]) : false;
                    return (
                      <Box key={label} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: done ? 'primary.contrastText' : 'background.paper', border: 1, borderColor: done ? 'primary.main' : 'divider', boxShadow: done ? 1 : 0 }} />
                    );
                  })}
                </Box>
              </Box>
              {/* Labels */}
              <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                {['Intro','House','Senate','President'].map((s, idx)=> {
                  const done = Array.isArray(stepStates) ? Boolean(stepStates[idx]) : false;
                  return (
                    <Typography key={s} variant="caption" sx={{ fontWeight: done ? 600 : 400 }} color={done ? 'text.primary' : 'text.secondary'}>
                      {s}
                    </Typography>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* Row 2 — Primary actions */}
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
            <Button size="small" variant={isFollowing ? 'outlined' : 'contained'} color={isFollowing ? 'inherit' : 'primary'} onClick={onToggleFollow}>
              {isFollowing ? 'Following' : 'Follow'}
            </Button>
            <Button size="small" variant="contained" color="secondary" onClick={onChat}>Ask about this bill</Button>
          </Stack>

          {/* Row 3 — Secondary toolbar */}
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* Segmented reading mode */}
            <ToggleButtonGroup
              value={readingMode}
              exclusive
              size="small"
              onChange={(_, v) => v && onReadingModeChange && onReadingModeChange(v)}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
            >
              <ToggleButton value="default">Default</ToggleButton>
              <ToggleButton value="eli5">ELI5</ToggleButton>
              <ToggleButton value="expert">Expert</ToggleButton>
              <ToggleButton value="proscons">Pros & Cons</ToggleButton>
            </ToggleButtonGroup>

            {/* Quiet icon/text buttons */}
            <Tooltip title="Share"><span><IconButton size="small" aria-label="Share bill" onClick={onShare}><ShareOutlinedIcon /></IconButton></span></Tooltip>
            <Tooltip title="Compare versions"><span><IconButton size="small" aria-label="Compare bills" onClick={onCompare}><CompareArrowsOutlinedIcon /></IconButton></span></Tooltip>
            <Tooltip title="Refresh"><span><IconButton size="small" aria-label="Refresh bill" onClick={onRefresh}><RefreshOutlinedIcon /></IconButton></span></Tooltip>
            <Button size="small" variant="outlined" startIcon={<SummarizeOutlinedIcon />} onClick={onGenerateSummary}>Generate AI summary</Button>

            {/* More menu */}
            <IconButton size="small" aria-label="More bill tools" onClick={handleOpen}><MoreHorizIcon /></IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose} keepMounted>
              <MenuItem onClick={() => { handleClose(); onMoreAction && onMoreAction('embed'); }}>Embed</MenuItem>
              <MenuItem onClick={() => { handleClose(); onMoreAction && onMoreAction('json'); }}>Export JSON</MenuItem>
              <MenuItem onClick={() => { handleClose(); onMoreAction && onMoreAction('csv'); }}>Export CSV</MenuItem>
              <MenuItem onClick={() => { handleClose(); onMoreAction && onMoreAction('rss'); }}>RSS</MenuItem>
            </Menu>

            {/* Metadata chips aligned right */}
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              {metadata.map((m, i) => (
                <Chip key={i} size="small" label={m.label} variant={m.variant === 'outlined' ? 'outlined' : 'filled'} sx={{ bgcolor: m.variant === 'outlined' ? undefined : 'action.hover' }} />
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>
    </header>
  );
};

export default BillHero;
